import React, { useEffect, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useIntl } from 'react-intl';

import { Button, FieldGroup, Input, InputProps, ValidationMessage } from '@xsolla/ui-core';
import { Trash } from '@xsolla/ui-icons';

import { isDisabledFieldValue } from './isDisabledFieldValue';
import { AddButton, AddButtonWrapper } from './styles';

type Props = InputProps & {
  label: string;
  addLabel: string;
  placeholder: string;
  name: string;
  disabledValues?: string[];
};

const ButtonDeleteLink = ({
  index,
  onClick,
  showAll
}: {
  index: number;
  onClick: (index: number) => void;
  showAll: boolean;
}) => {
  if (index > 9 && !showAll) {
    return null;
  }

  return (
    <Button look="icon" palette="brand" size="md" onClick={() => onClick(index)}>
      <Trash />
    </Button>
  );
};

const LinkFieldArray = ({
  label,
  addLabel,
  name,
  placeholder,
  disabledValues
}: Props): JSX.Element => {
  const form = useForm();
  const intl = useIntl();
  const [showAll, setShowAll] = useState(false);
  const { errors, submitFailed } = useFormState({
    subscription: { errors: true, submitFailed: true }
  });

  useEffect(() => {
    const value = form.getState().values[name];

    if (value && !Array.isArray(value)) {
      form.change(name, value.split(' '));
    }
  }, []);

  useEffect(() => {
    if (showAll || !submitFailed) return;

    const fieldErrors = errors?.[name];
    if (Array.isArray(fieldErrors) && fieldErrors.some((err, idx) => idx > 9 && err)) {
      setShowAll(true);
    }
  }, [errors, name, showAll, submitFailed]);

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  return (
    <>
      <FieldGroup label={label}>
        <FieldArray name={name}>
          {({ fields, meta }) => {
            const fieldsArray = fields.map((field) => field);

            return (
              <>
                <AddButtonWrapper>
                  <AddButton
                    dataTestId={`btn-add-${name}`}
                    hasFields={Boolean(fieldsArray.length)}
                    onClick={() => form.mutators.push(name, '')}
                    error={
                      fieldsArray.length === 0 &&
                      meta.touched &&
                      meta.error &&
                      !Array.isArray(meta.error)
                    }
                    type="button">
                    {addLabel}
                  </AddButton>
                </AddButtonWrapper>

                {fieldsArray.map((link: string, index: number) => (
                  <FieldGroup size="sm" key={link}>
                    <Field name={`${link}`}>
                      {({ input, meta: fieldMeta }) => {
                        const values = (fields.value ?? []) as string[];
                        const isDisabled = isDisabledFieldValue(
                          input.value,
                          index,
                          values,
                          disabledValues
                        );
                        return (
                          <Input
                            style={
                              index > 9 && !showAll
                                ? {
                                    display: 'none'
                                  }
                                : {}
                            }
                            disabled={isDisabled}
                            extraAlign="right"
                            errorMessage={
                              fieldMeta.touched &&
                              meta.error &&
                              meta.error[index] &&
                              intl.formatMessage({ id: meta.error[index] })
                            }
                            extra={
                              isDisabled
                                ? undefined
                                : () => (
                                    <ButtonDeleteLink
                                      index={index}
                                      onClick={() => fields.remove(index)}
                                      showAll={showAll}
                                    />
                                  )
                            }
                            extraAllowClick
                            placeholder={placeholder}
                            uiSize="md"
                            {...input}
                          />
                        );
                      }}
                    </Field>
                  </FieldGroup>
                ))}

                {fieldsArray.length > 10 && (
                  <Button onClick={toggleShowAll} type="button">
                    {showAll
                      ? intl.messages['social-connection.card.button.show-less']
                      : intl.messages['social-connection.card.button.show-more']}
                  </Button>
                )}

                {fieldsArray.length === 0 &&
                  meta.touched &&
                  meta.error &&
                  !Array.isArray(meta.error) && (
                    <ValidationMessage>{intl.formatMessage({ id: meta.error })}</ValidationMessage>
                  )}
              </>
            );
          }}
        </FieldArray>
      </FieldGroup>
    </>
  );
};

export default LinkFieldArray;
